// src/pages/retail-shops/WalletManagement.jsx
import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import axios from 'axios';
import {
  Box, Typography, Grid, Button, Chip, IconButton, Tooltip, Dialog,
  DialogTitle, DialogContent, DialogActions, TextField, MenuItem,
  FormControl, InputLabel, Select, LinearProgress, Alert, Snackbar,
  TablePagination, InputAdornment, CircularProgress, Fade, Paper,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Avatar, alpha, Divider, Stack, Card, CardContent, Skeleton,
  Tabs, Tab,
} from '@mui/material';
import {
  Search as SearchIcon,
  Clear as ClearIcon,
  Close as CloseIcon,
  Add as AddIcon,
  AccountBalanceWallet as WalletIcon,
  PersonSearch as PersonSearchIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  Lock as LockIcon,
  LockOpen as LockOpenIcon,
  History as HistoryIcon,
  Refresh as RefreshIcon,
  AttachMoney as MoneyIcon,
  SwapHoriz as SwapIcon,
  ArrowForward as ArrowIcon,
  Undo as UndoIcon,
  RemoveCircle as RemoveCircleIcon,
} from '@mui/icons-material';

import '../../styles/Waiters.css';
import Sidebar from '../../components/finance/Sidebar';
import Header from '../../components/finance/Header';
import Footer from '../../components/finance/Footer';
import { ACCE_BRAND, glossyButton } from '../../theme/colors';

// ════════════════════════════════════════════
// API
// ════════════════════════════════════════════
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://127.0.0.1:8000';
const API_PREFIX   = process.env.REACT_APP_API_PREFIX   || '/api';
const FULL_API_URL = `${API_BASE_URL}${API_PREFIX}`;

const api = axios.create({
  baseURL: FULL_API_URL,
  headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
  timeout: 30000,
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('access_token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

/**
 * Robust error extractor.
 * Handles:
 *  - axios Network Error
 *  - Laravel validation 422 with `errors` object
 *  - Laravel JSON `message` field
 *  - HTML 500 responses (extract <title> tag)
 *  - Plain strings
 */
const extractError = (err) => {
  if (!err) return 'Request failed';

  // Network / timeout
  if (err.code === 'ECONNABORTED') return 'Request timed out. Please try again.';
  if (err.message === 'Network Error') return 'Network error — is the server running?';

  const res = err.response;
  if (!res) return err.message || 'Request failed';

  const d = res.data;

  // Plain string body
  if (typeof d === 'string') {
    // Try to extract <title> from an HTML error page
    const titleMatch = d.match(/<title>([^<]+)<\/title>/i);
    if (titleMatch) return titleMatch[1].trim();

    // Fall back to first 200 chars
    return d.slice(0, 200) || `Request failed (${res.status})`;
  }

  // JSON body
  if (d && typeof d === 'object') {
    if (d.errors) {
      const first = Object.values(d.errors)[0];
      return Array.isArray(first) ? first[0] : String(first);
    }
    if (d.message) return d.message;
    if (d.error) return String(d.error);
  }

  // Fall back on HTTP status
  return `Request failed (HTTP ${res.status})`;
};

// ────────────────────────────────────────
// Wallet API
// ────────────────────────────────────────
const apiListWallets = async (params = {}) => {
  try {
    const { data } = await api.get('/tuckshop/wallets', { params });
    return { success: true, ...data };
  } catch (err) { return { success: false, error: extractError(err) }; }
};

const apiLookupUser = async ({ staffId, name }) => {
  try {
    const params = {};
    if (staffId) params.staff_id = staffId;
    if (name) params.name = name;
    const { data } = await api.get('/tuckshop/wallets/lookup', { params });
    return { success: true, ...data };
  } catch (err) {
    if (err.response?.status === 404) {
      return { success: false, notFound: true, error: extractError(err) };
    }
    return { success: false, error: extractError(err) };
  }
};

const apiFundWallet = async (payload) => {
  try {
    const { data } = await api.post('/tuckshop/wallets/fund', payload);
    return { success: true, ...data };
  } catch (err) { return { success: false, error: extractError(err) }; }
};

const apiWalletDetail = async (staffId, params = {}) => {
  try {
    const { data } = await api.get(`/tuckshop/wallets/${staffId}`, { params });
    return { success: true, ...data };
  } catch (err) { return { success: false, error: extractError(err) }; }
};

const apiWalletStats = async () => {
  try {
    const { data } = await api.get('/tuckshop/wallets/stats/summary');
    return { success: true, stats: data };
  } catch (err) { return { success: false, error: extractError(err) }; }
};

const apiToggleLock = async (staffId) => {
  try {
    const { data } = await api.post(`/tuckshop/wallets/${staffId}/toggle-lock`);
    return { success: true, ...data };
  } catch (err) { return { success: false, error: extractError(err) }; }
};

// ── Advanced operations ──
const apiTransferWallet = async (payload) => {
  try {
    const { data } = await api.post('/tuckshop/wallets/transfer', payload);
    return { success: true, ...data };
  } catch (err) { return { success: false, error: extractError(err) }; }
};

const apiReverseLast = async (staffId, payload) => {
  try {
    const { data } = await api.post(`/tuckshop/wallets/${staffId}/reverse-last`, payload);
    return { success: true, ...data };
  } catch (err) { return { success: false, error: extractError(err) }; }
};

const apiManualDebit = async (staffId, payload) => {
  try {
    const { data } = await api.post(`/tuckshop/wallets/${staffId}/debit`, payload);
    return { success: true, ...data };
  } catch (err) { return { success: false, error: extractError(err) }; }
};

// ════════════════════════════════════════════
// UTILITIES
// ════════════════════════════════════════════
const formatCurrency = (v) => {
  if (v === null || v === undefined || v === '') return '₦0.00';
  return `₦${Number(v).toLocaleString('en-NG', {
    minimumFractionDigits: 2, maximumFractionDigits: 2,
  })}`;
};

const formatCompactCurrency = (v) => {
  const n = Number(v) || 0;
  if (Math.abs(n) >= 1_000_000) return `₦${(n / 1_000_000).toFixed(2)}M`;
  if (Math.abs(n) >= 1_000)     return `₦${(n / 1_000).toFixed(1)}K`;
  return `₦${n.toFixed(0)}`;
};

const formatDateTime = (d) => {
  if (!d) return '—';
  return new Date(d).toLocaleString('en-US', {
    year: 'numeric', month: 'short', day: '2-digit',
    hour: '2-digit', minute: '2-digit', hour12: true,
  });
};

const safeNumber = (v, fallback = 0) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : fallback;
};

const round2 = (n) => Math.round(safeNumber(n) * 100) / 100;

const initials = (name) => {
  if (!name) return '?';
  return name
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((s) => s[0])
    .join('')
    .toUpperCase();
};

const generateIdempotencyKey = (prefix = 'op') =>
  `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;

/** Human-friendly label for a transaction_type */
const prettifyTxType = (t) => {
  if (!t) return '';
  return String(t).replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
};

// ════════════════════════════════════════════
// SUB-COMPONENTS
// ════════════════════════════════════════════
const StatCard = React.memo(({ label, value, sub, icon, color }) => (
  <Card
    elevation={0}
    sx={{
      borderRadius: '14px',
      border: `1px solid ${alpha(color, 0.15)}`,
      bgcolor: '#fff',
      height: '100%',
      position: 'relative',
      overflow: 'hidden',
      transition: 'all 0.25s ease',
      '&:hover': {
        transform: 'translateY(-3px)',
        boxShadow: `0 12px 30px ${alpha(color, 0.12)}`,
        borderColor: alpha(color, 0.3),
      },
      '&::before': {
        content: '""',
        position: 'absolute',
        left: 0, top: 0, bottom: 0, width: 4,
        bgcolor: color,
      },
    }}
  >
    <CardContent sx={{ p: 2, pl: 2.4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1.5 }}>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography
            variant="caption"
            sx={{
              color: '#64748B', fontWeight: 700, textTransform: 'uppercase',
              letterSpacing: '0.6px', fontSize: '0.65rem',
              fontFamily: '"Inter", sans-serif', display: 'block', mb: 0.5,
            }}
          >
            {label}
          </Typography>
          <Typography
            variant="h6"
            fontWeight={800}
            sx={{
              fontSize: { xs: '1.05rem', sm: '1.3rem' },
              color: '#0F172A', lineHeight: 1.15,
              fontFamily: '"Inter", sans-serif', letterSpacing: '-0.5px',
            }}
          >
            {value}
          </Typography>
          {sub && (
            <Typography
              variant="caption"
              sx={{
                color: '#64748B', fontWeight: 600, fontSize: '0.68rem',
                fontFamily: '"Inter", sans-serif', display: 'block', mt: 0.4,
              }}
            >
              {sub}
            </Typography>
          )}
        </Box>
        <Avatar sx={{
          bgcolor: alpha(color, 0.1), color,
          width: 40, height: 40, borderRadius: '10px',
        }}>
          {icon}
        </Avatar>
      </Box>
    </CardContent>
  </Card>
));
StatCard.displayName = 'StatCard';

// ════════════════════════════════════════════
// FUNDING DIALOG
// ════════════════════════════════════════════
const FundingDialog = ({ open, onClose, onFunded, pushSnackbar }) => {
  const [searchMode, setSearchMode] = useState('staff_id');
  const [lookupValue, setLookupValue] = useState('');
  const [lookupLoading, setLookupLoading] = useState(false);
  const [lookupResult, setLookupResult] = useState(null);
  const [lookupError, setLookupError] = useState('');

  const [amount, setAmount] = useState('');
  const [description, setDescription] = useState('Manual wallet funding');
  const [idempotencyKey, setIdempotencyKey] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const amountInputRef = useRef(null);
  const lookupDebounceRef = useRef(null);
  const lookupAbortRef = useRef(null);

  useEffect(() => {
    if (open) {
      setSearchMode('staff_id');
      setLookupValue('');
      setLookupResult(null);
      setLookupError('');
      setLookupLoading(false);
      setAmount('');
      setDescription('Manual wallet funding');
      setIdempotencyKey(generateIdempotencyKey('fund'));
      setSubmitting(false);
    }
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const value = lookupValue.trim();

    if (lookupDebounceRef.current) {
      clearTimeout(lookupDebounceRef.current);
      lookupDebounceRef.current = null;
    }

    if (!value) {
      setLookupResult(null);
      setLookupError('');
      setLookupLoading(false);
      return undefined;
    }

    const requestId = Date.now() + Math.random();
    lookupAbortRef.current = requestId;

    setLookupLoading(true);
    setLookupError('');

    lookupDebounceRef.current = setTimeout(async () => {
      const payload = searchMode === 'staff_id' ? { staffId: value } : { name: value };
      const res = await apiLookupUser(payload);

      if (lookupAbortRef.current !== requestId) return;

      setLookupLoading(false);

      if (!res.success) {
        setLookupResult(null);
        setLookupError(
          res.notFound
            ? `No student matches that ${searchMode === 'staff_id' ? 'student ID' : 'name'}.`
            : (res.error || 'Lookup failed.')
        );
        return;
      }

      setLookupResult(res);
      setLookupError('');
      setTimeout(() => amountInputRef.current?.focus(), 150);
    }, 450);

    return () => {
      if (lookupDebounceRef.current) {
        clearTimeout(lookupDebounceRef.current);
        lookupDebounceRef.current = null;
      }
    };
  }, [lookupValue, searchMode, open]);

  useEffect(() => {
    if (!open) {
      if (lookupDebounceRef.current) {
        clearTimeout(lookupDebounceRef.current);
        lookupDebounceRef.current = null;
      }
      lookupAbortRef.current = null;
    }
  }, [open]);

  const handleLookup = useCallback(() => {
    if (!lookupValue.trim()) return;
    const v = lookupValue;
    setLookupValue('');
    setTimeout(() => setLookupValue(v), 0);
  }, [lookupValue]);

  const handleSubmit = useCallback(async () => {
    if (!lookupResult) {
      pushSnackbar('Look up a student first.', 'warning');
      return;
    }
    const rawAmount = String(amount).trim();
    if (!/^\d+(\.\d{1,2})?$/.test(rawAmount) || Number(rawAmount) <= 0) {
      pushSnackbar('Enter a valid amount greater than 0 (max 2 decimals).', 'error');
      return;
    }

    setSubmitting(true);
    const res = await apiFundWallet({
      staff_id: lookupResult.user.staff_id,
      amount: rawAmount,
      description: description?.trim() || 'Manual wallet funding',
      idempotency_key: idempotencyKey,
    });
    setSubmitting(false);

    if (!res.success) {
      pushSnackbar(res.error, 'error');
      return;
    }

    pushSnackbar(`✅ ${formatCurrency(res.wallet.balance)} in ${res.wallet.holder_name}'s wallet.`, 'success');
    onFunded?.(res);
    onClose();
  }, [lookupResult, amount, description, idempotencyKey, pushSnackbar, onFunded, onClose]);

  const newBalance = useMemo(() => {
    if (!lookupResult) return null;
    const current = safeNumber(lookupResult.wallet.balance);
    const add = safeNumber(amount);
    if (!Number.isFinite(add) || add <= 0) return current;
    return round2(current + add);
  }, [lookupResult, amount]);

  return (
    <Dialog
      open={open}
      onClose={submitting ? undefined : onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: { borderRadius: 3 } }}
    >
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2 }}>
          <Avatar sx={{ bgcolor: alpha(ACCE_BRAND.orange, 0.12), color: ACCE_BRAND.orange, width: 40, height: 40 }}>
            <WalletIcon />
          </Avatar>
          <Box>
            <Typography variant="h6" fontWeight={800} fontFamily='"Inter", sans-serif'>
              Fund Wallet
            </Typography>
            <Typography variant="caption" color="text.secondary" fontFamily='"Inter", sans-serif'>
              Credit a student wallet by entering their student ID or name
            </Typography>
          </Box>
        </Box>
        <IconButton onClick={onClose} disabled={submitting}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ p: { xs: 2, sm: 3 } }}>
        {/* STEP 1: LOOKUP */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" fontWeight={800} gutterBottom
            sx={{ fontFamily: '"Inter", sans-serif', color: '#0F172A', mb: 1.5 }}>
            1. Find Student
          </Typography>

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'flex-start' }}>
            <FormControl size="small" sx={{ minWidth: 140 }}>
              <InputLabel sx={{ fontWeight: 700 }}>Search by</InputLabel>
              <Select
                value={searchMode}
                label="Search by"
                onChange={(e) => {
                  setSearchMode(e.target.value);
                  setLookupValue('');
                  setLookupResult(null);
                  setLookupError('');
                }}
                sx={{ borderRadius: 2, fontWeight: 600 }}
              >
                <MenuItem value="staff_id" sx={{ fontWeight: 600 }}>Student ID</MenuItem>
                <MenuItem value="name" sx={{ fontWeight: 600 }}>Name</MenuItem>
              </Select>
            </FormControl>

            <TextField
              size="small"
              fullWidth
              autoFocus
              value={lookupValue}
              onChange={(e) => {
                setLookupValue(e.target.value);
                setLookupError('');
              }}
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleLookup(); } }}
              placeholder={searchMode === 'staff_id' ? 'e.g. ADM001' : 'e.g. System Admin'}
              label={searchMode === 'staff_id' ? 'Student ID' : 'Full or partial name'}
              InputLabelProps={{ shrink: true }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <PersonSearchIcon sx={{ color: alpha(ACCE_BRAND.orange, 0.7) }} />
                  </InputAdornment>
                ),
                endAdornment: lookupLoading && (
                  <InputAdornment position="end">
                    <CircularProgress size={16} />
                  </InputAdornment>
                ),
              }}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
            />

            <Button
              variant="contained"
              onClick={handleLookup}
              disabled={lookupLoading || !lookupValue.trim()}
              startIcon={lookupLoading ? <CircularProgress size={16} color="inherit" /> : <SearchIcon />}
              sx={{
                ...glossyButton(ACCE_BRAND.orange, ACCE_BRAND.orangeDark, ACCE_BRAND.orangeLight),
                minWidth: 120, height: 40, textTransform: 'none', fontWeight: 700,
                borderRadius: 2,
              }}
            >
              {lookupLoading ? 'Looking…' : 'Look up'}
            </Button>
          </Stack>

          {lookupError && (
            <Alert severity="error" sx={{ mt: 1.5, borderRadius: 2 }}>
              {lookupError}
            </Alert>
          )}
        </Box>

        {/* STEP 2: DETAILS */}
        {lookupResult && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" fontWeight={800} gutterBottom
              sx={{ fontFamily: '"Inter", sans-serif', color: '#0F172A', mb: 1.5 }}>
              2. Student &amp; Wallet Details
            </Typography>

            <Paper
              variant="outlined"
              sx={{
                p: 2, borderRadius: 2,
                borderColor: alpha(ACCE_BRAND.orange, 0.25),
                bgcolor: alpha(ACCE_BRAND.orange, 0.02),
              }}
            >
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center">
                <Avatar sx={{ width: 56, height: 56, bgcolor: ACCE_BRAND.orange, fontWeight: 800, fontSize: '1.1rem' }}>
                  {initials(lookupResult.user.full_name)}
                </Avatar>

                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="subtitle1" fontWeight={800}
                    sx={{ fontFamily: '"Inter", sans-serif', color: '#0F172A' }}>
                    {lookupResult.user.full_name}
                  </Typography>
                  <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mt: 0.5 }}>
                    <Chip size="small" label={lookupResult.user.staff_id}
                      sx={{ fontWeight: 700, fontSize: '0.68rem', bgcolor: alpha(ACCE_BRAND.navy || '#2A3A54', 0.1), color: ACCE_BRAND.navy || '#2A3A54' }} />
                    <Chip size="small" label={lookupResult.user.role || 'Student'}
                      sx={{ fontWeight: 700, fontSize: '0.68rem', bgcolor: alpha('#4caf50', 0.1), color: '#2e7d32' }} />
                    {lookupResult.user.department && (
                      <Chip size="small" label={lookupResult.user.department}
                        sx={{ fontWeight: 700, fontSize: '0.68rem', bgcolor: alpha('#2196f3', 0.1), color: '#1565c0' }} />
                    )}
                    {!lookupResult.user.is_active && (
                      <Chip size="small" icon={<WarningIcon sx={{ fontSize: 12 }} />} label="Inactive"
                        sx={{ fontWeight: 700, fontSize: '0.68rem', bgcolor: alpha('#f44336', 0.1), color: '#c62828' }} />
                    )}
                  </Stack>
                  <Typography variant="caption" color="text.secondary"
                    sx={{ mt: 0.5, display: 'block', fontFamily: '"Inter", sans-serif' }}>
                    {lookupResult.user.email} · {lookupResult.user.phone || 'no phone'}
                  </Typography>
                </Box>

                <Box sx={{ textAlign: { xs: 'left', sm: 'right' }, minWidth: 180 }}>
                  <Typography variant="caption" sx={{
                    color: '#64748B', fontWeight: 700, textTransform: 'uppercase',
                    letterSpacing: '0.5px', fontSize: '0.62rem', fontFamily: '"Inter", sans-serif',
                  }}>
                    Current Balance
                  </Typography>
                  <Typography variant="h5" fontWeight={800}
                    sx={{ color: ACCE_BRAND.orange, fontFamily: '"Inter", sans-serif' }}>
                    {formatCurrency(lookupResult.wallet.balance)}
                  </Typography>
                </Box>
              </Stack>

              <Divider sx={{ my: 2 }} />

              <Grid container spacing={2}>
                <Grid item xs={6} sm={4}>
                  <Typography variant="caption" color="text.secondary" fontWeight={600}>Lifetime credited</Typography>
                  <Typography fontWeight={800} sx={{ color: '#2e7d32', fontFamily: '"Inter", sans-serif' }}>
                    {formatCurrency(lookupResult.wallet.total_credited)}
                  </Typography>
                </Grid>
                <Grid item xs={6} sm={4}>
                  <Typography variant="caption" color="text.secondary" fontWeight={600}>Lifetime debited</Typography>
                  <Typography fontWeight={800} sx={{ color: '#c62828', fontFamily: '"Inter", sans-serif' }}>
                    {formatCurrency(lookupResult.wallet.total_debited)}
                  </Typography>
                </Grid>
                <Grid item xs={6} sm={4}>
                  <Typography variant="caption" color="text.secondary" fontWeight={600}>Status</Typography>
                  <Chip
                    size="small"
                    icon={lookupResult.wallet.is_locked ? <LockIcon sx={{ fontSize: 12 }} /> : <LockOpenIcon sx={{ fontSize: 12 }} />}
                    label={lookupResult.wallet.is_locked ? 'Locked' : 'Active'}
                    sx={{
                      fontWeight: 700, fontSize: '0.68rem',
                      bgcolor: alpha(lookupResult.wallet.is_locked ? '#f44336' : '#4caf50', 0.1),
                      color: lookupResult.wallet.is_locked ? '#c62828' : '#2e7d32',
                    }}
                  />
                </Grid>
              </Grid>

              {lookupResult.wallet.is_locked && (
                <Alert severity="warning" sx={{ mt: 2, borderRadius: 2 }}>
                  This wallet is <strong>locked</strong>. Unlock it before funding.
                </Alert>
              )}
            </Paper>
          </Box>
        )}

        {/* STEP 3: AMOUNT */}
        {lookupResult && !lookupResult.wallet.is_locked && (
          <Box>
            <Typography variant="subtitle2" fontWeight={800} gutterBottom
              sx={{ fontFamily: '"Inter", sans-serif', color: '#0F172A', mb: 1.5 }}>
              3. Amount &amp; Description
            </Typography>

            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  inputRef={amountInputRef}
                  fullWidth required type="number"
                  label="Amount to Credit"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  placeholder="0.00"
                  inputProps={{ min: 0.01, step: 0.01, style: { fontSize: '1.2rem', fontWeight: 700 } }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Typography sx={{ fontWeight: 800, color: ACCE_BRAND.orange }}>₦</Typography>
                      </InputAdornment>
                    ),
                  }}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth label="Description"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="e.g. Monthly student allowance"
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth size="small"
                  label="Idempotency key (auto-generated)"
                  value={idempotencyKey}
                  onChange={(e) => setIdempotencyKey(e.target.value)}
                  helperText="Prevents double-crediting if the request is retried."
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  InputProps={{ sx: { fontFamily: 'monospace', fontSize: '0.8rem' } }}
                />
              </Grid>
            </Grid>

            {Number(amount) > 0 && (
              <Alert severity="info" icon={<TrendingUpIcon />} sx={{ mt: 2, borderRadius: 2 }}>
                New balance will be <strong>{formatCurrency(newBalance)}</strong>
                {' '}(from {formatCurrency(lookupResult.wallet.balance)})
              </Alert>
            )}
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 2, gap: 1 }}>
        <Button onClick={onClose} disabled={submitting} color="inherit" sx={{ textTransform: 'none', fontWeight: 700 }}>
          Cancel
        </Button>
        <Button
          variant="contained"
          disabled={submitting || !lookupResult || lookupResult.wallet.is_locked || !amount || Number(amount) <= 0}
          onClick={handleSubmit}
          startIcon={submitting ? <CircularProgress size={18} color="inherit" /> : <CheckCircleIcon />}
          sx={{
            ...glossyButton(ACCE_BRAND.orange, ACCE_BRAND.orangeDark, ACCE_BRAND.orangeLight),
            textTransform: 'none', fontWeight: 800, minWidth: 160,
          }}
        >
          {submitting ? 'Processing…' : `Credit ${amount ? formatCurrency(amount) : ''}`}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ════════════════════════════════════════════
// WALLET DETAIL DIALOG (with Manual Adjustments tab)
// ════════════════════════════════════════════
const ADJUSTMENT_TYPES = 'manual_debit,reversal,funding,transfer_in,transfer_out';

const WalletDetailDialog = ({ open, staffId, onClose, pushSnackbar }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [direction, setDirection] = useState('all');
  const [tab, setTab] = useState('all'); // 'all' | 'adjustments'

  const fetchDetail = useCallback(async () => {
    if (!staffId) return;
    setLoading(true);
    const params = { page: page + 1, page_size: rowsPerPage };
    if (direction !== 'all') params.direction = direction;
    if (tab === 'adjustments') params.transaction_type = ADJUSTMENT_TYPES;

    const res = await apiWalletDetail(staffId, params);
    setLoading(false);
    if (res.success) setData(res);
    else pushSnackbar(res.error || 'Failed to load wallet', 'error');
  }, [staffId, page, rowsPerPage, direction, tab, pushSnackbar]);

  // Reset + fetch when dialog opens
  useEffect(() => {
    if (open && staffId) {
      setPage(0);
      setDirection('all');
      setTab('all');
      // Trigger fetch after state settles
      setTimeout(fetchDetail, 0);
    }
  }, [open, staffId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Refetch on filter / pagination change
  useEffect(() => {
    if (open && staffId) fetchDetail();
  }, [page, rowsPerPage, direction, tab]); // eslint-disable-line react-hooks/exhaustive-deps

  const wallet = data?.wallet;
  const txns   = data?.transactions;
  const showReason = tab === 'adjustments';

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: { borderRadius: 3, maxHeight: '92vh' } }}
    >
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2 }}>
          <Avatar sx={{ bgcolor: alpha(ACCE_BRAND.orange, 0.12), color: ACCE_BRAND.orange }}>
            <HistoryIcon />
          </Avatar>
          <Box>
            <Typography variant="h6" fontWeight={800} fontFamily='"Inter", sans-serif'>
              {wallet?.holder_name || 'Wallet'}
            </Typography>
            <Typography variant="caption" color="text.secondary" fontFamily='"Inter", sans-serif'>
              {wallet?.staff_id} · {wallet?.currency || 'NGN'}
            </Typography>
          </Box>
        </Box>
        <IconButton onClick={onClose}><CloseIcon /></IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ p: 0 }}>
        {loading && !wallet ? (
          <Box sx={{ p: 3 }}>
            <Skeleton variant="rectangular" height={80} sx={{ borderRadius: 2, mb: 2 }} />
            <Skeleton variant="rectangular" height={200} sx={{ borderRadius: 2 }} />
          </Box>
        ) : wallet ? (
          <>
            <Box sx={{ p: 2.5, bgcolor: alpha(ACCE_BRAND.orange, 0.03), borderBottom: '1px solid', borderColor: 'divider' }}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={3}>
                  <Typography variant="caption" color="text.secondary" fontWeight={700}>Current balance</Typography>
                  <Typography variant="h5" fontWeight={800} sx={{ color: ACCE_BRAND.orange, fontFamily: '"Inter", sans-serif' }}>
                    {formatCurrency(wallet.balance)}
                  </Typography>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Typography variant="caption" color="text.secondary" fontWeight={700}>Lifetime credited</Typography>
                  <Typography fontWeight={800} sx={{ color: '#2e7d32', fontFamily: '"Inter", sans-serif' }}>
                    {formatCurrency(wallet.total_credited)}
                  </Typography>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Typography variant="caption" color="text.secondary" fontWeight={700}>Lifetime debited</Typography>
                  <Typography fontWeight={800} sx={{ color: '#c62828', fontFamily: '"Inter", sans-serif' }}>
                    {formatCurrency(wallet.total_debited)}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={3}>
                  <Typography variant="caption" color="text.secondary" fontWeight={700}>Last activity</Typography>
                  <Typography fontWeight={700} sx={{ fontSize: '0.8rem', fontFamily: '"Inter", sans-serif' }}>
                    {formatDateTime(wallet.last_transaction_at)}
                  </Typography>
                </Grid>
              </Grid>
            </Box>

            {/* Tabs */}
            <Box sx={{ borderBottom: 1, borderColor: 'divider', bgcolor: '#fff' }}>
              <Tabs
                value={tab}
                onChange={(_, v) => { setTab(v); setPage(0); }}
                sx={{
                  px: 2,
                  '& .MuiTab-root': {
                    textTransform: 'none', fontWeight: 700,
                    fontFamily: '"Inter", sans-serif', minHeight: 48,
                  },
                }}
              >
                <Tab value="all" label="All Transactions" />
                <Tab value="adjustments" label="Manual Adjustments" />
              </Tabs>
            </Box>

            {/* Filter */}
            <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
              <Typography variant="subtitle2" fontWeight={800} sx={{ fontFamily: '"Inter", sans-serif' }}>
                {tab === 'all' ? 'Transaction history' : 'Adjustments & audit log'}
              </Typography>
              <FormControl size="small" sx={{ minWidth: 140 }}>
                <InputLabel>Direction</InputLabel>
                <Select
                  value={direction}
                  label="Direction"
                  onChange={(e) => { setDirection(e.target.value); setPage(0); }}
                  sx={{ borderRadius: 2 }}
                >
                  <MenuItem value="all" sx={{ fontWeight: 600 }}>All</MenuItem>
                  <MenuItem value="credit" sx={{ fontWeight: 600 }}>Credits</MenuItem>
                  <MenuItem value="debit" sx={{ fontWeight: 600 }}>Debits</MenuItem>
                </Select>
              </FormControl>
            </Box>

            {loading && <LinearProgress />}

            {txns?.data?.length ? (
              <>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ bgcolor: alpha(ACCE_BRAND.orange, 0.04) }}>
                        <TableCell sx={{ fontWeight: 800, fontFamily: '"Inter", sans-serif' }}>Date</TableCell>
                        <TableCell sx={{ fontWeight: 800, fontFamily: '"Inter", sans-serif' }}>Reference</TableCell>
                        <TableCell sx={{ fontWeight: 800, fontFamily: '"Inter", sans-serif' }}>Type</TableCell>
                        {showReason && (
                          <TableCell sx={{ fontWeight: 800, fontFamily: '"Inter", sans-serif' }}>Reason</TableCell>
                        )}
                        <TableCell align="right" sx={{ fontWeight: 800, fontFamily: '"Inter", sans-serif' }}>Amount</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 800, fontFamily: '"Inter", sans-serif' }}>Balance</TableCell>
                        <TableCell sx={{ fontWeight: 800, fontFamily: '"Inter", sans-serif' }}>By</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {txns.data.map((t) => {
                        const isCredit = t.direction === 'credit';
                        return (
                          <TableRow key={t.id} hover>
                            <TableCell sx={{ fontSize: '0.78rem' }}>
                              {formatDateTime(t.occurred_at || t.created_at)}
                            </TableCell>
                            <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.72rem', fontWeight: 700 }}>
                              {t.reference}
                            </TableCell>
                            <TableCell>
                              <Chip
                                size="small"
                                label={prettifyTxType(t.transaction_type)}
                                sx={{
                                  fontWeight: 700, fontSize: '0.65rem',
                                  bgcolor: alpha(isCredit ? '#4caf50' : '#f44336', 0.1),
                                  color: isCredit ? '#2e7d32' : '#c62828',
                                }}
                              />
                            </TableCell>
                            {showReason && (
                              <TableCell sx={{ fontSize: '0.78rem', maxWidth: 240 }}>
                                {t.reason_code && (
                                  <Chip
                                    size="small"
                                    label={t.reason_code}
                                    sx={{
                                      fontWeight: 700, fontSize: '0.65rem',
                                      bgcolor: alpha('#f57c00', 0.1), color: '#e65100',
                                      textTransform: 'capitalize', mr: 1,
                                    }}
                                  />
                                )}
                                <Typography variant="caption" sx={{ display: 'block', color: '#475569' }}>
                                  {t.reason_note || t.description || '—'}
                                </Typography>
                              </TableCell>
                            )}
                            <TableCell align="right" sx={{
                              fontWeight: 800,
                              color: isCredit ? '#2e7d32' : '#c62828',
                              fontFamily: '"Inter", sans-serif',
                            }}>
                              {isCredit ? '+' : '−'}{formatCurrency(t.amount)}
                            </TableCell>
                            <TableCell align="right" sx={{ fontWeight: 700, color: '#0F172A', fontFamily: '"Inter", sans-serif' }}>
                              {formatCurrency(t.balance_after)}
                            </TableCell>
                            <TableCell sx={{ fontSize: '0.72rem', color: '#64748B' }}>
                              {t.performed_by_name || t.source || '—'}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
                <TablePagination
                  rowsPerPageOptions={[5, 10, 25, 50]}
                  component="div"
                  count={txns.total}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  onPageChange={(_, p) => setPage(p)}
                  onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
                />
              </>
            ) : (
              <Box sx={{ textAlign: 'center', py: 6 }}>
                <WalletIcon sx={{ fontSize: 56, color: 'text.disabled', opacity: 0.3, mb: 1 }} />
                <Typography variant="body2" color="text.secondary" sx={{ fontFamily: '"Inter", sans-serif' }}>
                  {tab === 'adjustments' ? 'No manual adjustments recorded.' : 'No transactions yet.'}
                </Typography>
              </Box>
            )}
          </>
        ) : null}
      </DialogContent>

      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose} sx={{ textTransform: 'none', fontWeight: 700 }}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ════════════════════════════════════════════
// TRANSFER WALLET DIALOG
// ════════════════════════════════════════════
const TransferWalletDialog = ({ open, onClose, onTransferred, pushSnackbar, prefillFrom = null }) => {
  const [fromStaffId, setFromStaffId] = useState('');
  const [toStaffId, setToStaffId] = useState('');
  const [amount, setAmount] = useState('');
  const [description, setDescription] = useState('');
  const [idempotencyKey, setIdempotencyKey] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const [fromWallet, setFromWallet] = useState(null);
  const [toWallet, setToWallet] = useState(null);
  const [lookupError, setLookupError] = useState({ from: '', to: '' });
  const [lookingUp, setLookingUp] = useState({ from: false, to: false });

  const debounceRef = useRef({ from: null, to: null });
  const requestRef = useRef({ from: 0, to: 0 });

  useEffect(() => {
    if (open) {
      setFromStaffId(prefillFrom?.staff_id || '');
      setToStaffId('');
      setAmount('');
      setDescription('');
      setIdempotencyKey(generateIdempotencyKey('trf'));
      setSubmitting(false);
      setFromWallet(prefillFrom || null);
      setToWallet(null);
      setLookupError({ from: '', to: '' });
      setLookingUp({ from: false, to: false });
    }
  }, [open, prefillFrom]);

  const runLookup = useCallback((side, staffId) => {
    if (debounceRef.current[side]) clearTimeout(debounceRef.current[side]);
    if (!staffId.trim()) {
      if (side === 'from') setFromWallet(null); else setToWallet(null);
      setLookupError((e) => ({ ...e, [side]: '' }));
      setLookingUp((l) => ({ ...l, [side]: false }));
      return;
    }

    setLookingUp((l) => ({ ...l, [side]: true }));
    setLookupError((e) => ({ ...e, [side]: '' }));

    const requestId = Date.now() + Math.random();
    requestRef.current[side] = requestId;

    debounceRef.current[side] = setTimeout(async () => {
      const res = await apiLookupUser({ staffId: staffId.trim() });
      if (requestRef.current[side] !== requestId) return;

      setLookingUp((l) => ({ ...l, [side]: false }));

      if (!res.success) {
        if (side === 'from') setFromWallet(null); else setToWallet(null);
        setLookupError((e) => ({
          ...e,
          [side]: res.notFound ? 'No wallet found for that staff ID.' : (res.error || 'Lookup failed.'),
        }));
        return;
      }

      const found = {
        staff_id:    res.user.staff_id,
        holder_name: res.user.full_name,
        balance:     res.wallet.balance,
        is_locked:   res.wallet.is_locked,
      };
      if (side === 'from') setFromWallet(found); else setToWallet(found);
    }, 450);
  }, []);

  useEffect(() => {
    if (!open) return;
    if (fromStaffId.trim() !== (fromWallet?.staff_id || '')) runLookup('from', fromStaffId);
  }, [fromStaffId, open]); // eslint-disable-line

  useEffect(() => {
    if (!open) return;
    if (toStaffId.trim() !== (toWallet?.staff_id || '')) runLookup('to', toStaffId);
  }, [toStaffId, open]); // eslint-disable-line

  useEffect(() => {
    if (!open) {
      Object.values(debounceRef.current).forEach((t) => t && clearTimeout(t));
      debounceRef.current = { from: null, to: null };
    }
  }, [open]);

  const amountNum = Number(amount);
  const isValidAmount = /^\d+(\.\d{1,2})?$/.test(String(amount).trim()) && amountNum > 0;
  const insufficientBalance = fromWallet && isValidAmount && amountNum > Number(fromWallet.balance);
  const sameWallet = fromWallet && toWallet && fromWallet.staff_id === toWallet.staff_id;

  const canSubmit = useMemo(() => {
    if (!fromWallet || !toWallet) return false;
    if (sameWallet) return false;
    if (fromWallet.is_locked || toWallet.is_locked) return false;
    if (!isValidAmount) return false;
    if (insufficientBalance) return false;
    return true;
  }, [fromWallet, toWallet, sameWallet, isValidAmount, insufficientBalance]);

  const projectedFrom = fromWallet && isValidAmount
    ? round2(Number(fromWallet.balance) - amountNum)
    : null;
  const projectedTo = toWallet && isValidAmount
    ? round2(Number(toWallet.balance) + amountNum)
    : null;

  const handleSubmit = useCallback(async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    const res = await apiTransferWallet({
      from_staff_id: fromWallet.staff_id,
      to_staff_id: toWallet.staff_id,
      amount: String(amount).trim(),
      description: description?.trim() || undefined,
      idempotency_key: idempotencyKey,
    });
    setSubmitting(false);

    if (!res.success) {
      pushSnackbar(res.error || 'Transfer failed.', 'error');
      return;
    }

    pushSnackbar(`Transferred ${formatCurrency(amount)} to ${toWallet.holder_name}.`, 'success');
    onTransferred?.(res);
    onClose?.();
  }, [canSubmit, fromWallet, toWallet, amount, description, idempotencyKey, pushSnackbar, onTransferred, onClose]);

  const handleClose = () => { if (!submitting) onClose?.(); };

  const WalletPreview = ({ side, wallet, loading, error, projectedBalance }) => {
    const label = side === 'from' ? 'From (Debit)' : 'To (Credit)';
    const color = side === 'from' ? '#c62828' : '#2e7d32';
    return (
      <Paper
        variant="outlined"
        sx={{
          p: 1.5, borderRadius: 2, height: '100%',
          borderColor: alpha(color, 0.25),
          bgcolor: alpha(color, 0.02),
          display: 'flex', flexDirection: 'column', justifyContent: 'center',
          minHeight: 116,
        }}
      >
        <Typography variant="caption" sx={{
          color, fontWeight: 800, textTransform: 'uppercase',
          letterSpacing: '0.5px', fontSize: '0.65rem', mb: 0.5,
        }}>
          {label}
        </Typography>
        {loading ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CircularProgress size={16} />
            <Typography variant="body2" color="text.secondary">Looking up…</Typography>
          </Box>
        ) : error ? (
          <Typography variant="body2" color="error" sx={{ fontSize: '0.75rem' }}>{error}</Typography>
        ) : wallet ? (
          <>
            <Stack direction="row" alignItems="center" spacing={1}>
              <Avatar sx={{ width: 32, height: 32, bgcolor: alpha(color, 0.15), color, fontWeight: 800, fontSize: '0.75rem' }}>
                {initials(wallet.holder_name)}
              </Avatar>
              <Box sx={{ minWidth: 0 }}>
                <Typography fontWeight={800} sx={{ fontFamily: '"Inter", sans-serif', fontSize: '0.85rem' }} noWrap>
                  {wallet.holder_name}
                </Typography>
                <Typography variant="caption" sx={{ fontFamily: 'monospace', fontSize: '0.7rem', color: '#64748B' }}>
                  {wallet.staff_id}
                </Typography>
              </Box>
            </Stack>
            <Divider sx={{ my: 1 }} />
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Typography variant="caption" color="text.secondary" fontWeight={700}>Balance</Typography>
              <Typography fontWeight={800} sx={{ color, fontFamily: '"Inter", sans-serif', fontSize: '0.85rem' }}>
                {formatCurrency(wallet.balance)}
              </Typography>
            </Stack>
            {projectedBalance !== null && projectedBalance !== undefined && (
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Typography variant="caption" color="text.secondary" fontWeight={700}>After</Typography>
                <Typography fontWeight={700} sx={{ color: '#64748B', fontFamily: '"Inter", sans-serif', fontSize: '0.75rem' }}>
                  {formatCurrency(projectedBalance)}
                </Typography>
              </Stack>
            )}
            {wallet.is_locked && (
              <Typography variant="caption" sx={{ mt: 0.5, color: '#c62828', fontWeight: 700 }}>
                ⚠ Wallet is locked
              </Typography>
            )}
          </>
        ) : (
          <Typography variant="body2" color="text.disabled" sx={{ fontSize: '0.75rem' }}>
            Enter a staff ID
          </Typography>
        )}
      </Paper>
    );
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth
      PaperProps={{ sx: { borderRadius: 3 } }}>

      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2 }}>
          <Avatar sx={{ bgcolor: alpha('#1976d2', 0.12), color: '#1976d2' }}>
            <SwapIcon />
          </Avatar>
          <Box>
            <Typography variant="h6" fontWeight={800} fontFamily='"Inter", sans-serif'>
              Wallet to Wallet Transfer
            </Typography>
            <Typography variant="caption" color="text.secondary" fontFamily='"Inter", sans-serif'>
              Move funds atomically between two wallets
            </Typography>
          </Box>
        </Box>
        <IconButton onClick={handleClose} disabled={submitting}><CloseIcon /></IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ p: { xs: 2, sm: 3 } }}>
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={5}>
            <TextField
              fullWidth size="small"
              label="From Staff ID"
              value={fromStaffId}
              onChange={(e) => setFromStaffId(e.target.value)}
              placeholder="e.g. STU24739"
              disabled={submitting || !!prefillFrom}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <PersonSearchIcon sx={{ color: alpha('#c62828', 0.7), fontSize: 18 }} />
                  </InputAdornment>
                ),
                endAdornment: lookingUp.from && (
                  <InputAdornment position="end"><CircularProgress size={14} /></InputAdornment>
                ),
              }}
              sx={{ mb: 1.5, '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
            />
            <WalletPreview
              side="from"
              wallet={fromWallet}
              loading={lookingUp.from}
              error={lookupError.from}
              projectedBalance={projectedFrom}
            />
          </Grid>

          <Grid item xs={12} sm={2} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Avatar sx={{ bgcolor: alpha('#1976d2', 0.1), color: '#1976d2', width: 36, height: 36 }}>
              <ArrowIcon />
            </Avatar>
          </Grid>

          <Grid item xs={12} sm={5}>
            <TextField
              fullWidth size="small"
              label="To Staff ID"
              value={toStaffId}
              onChange={(e) => setToStaffId(e.target.value)}
              placeholder="e.g. STU23335"
              disabled={submitting}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <PersonSearchIcon sx={{ color: alpha('#2e7d32', 0.7), fontSize: 18 }} />
                  </InputAdornment>
                ),
                endAdornment: lookingUp.to && (
                  <InputAdornment position="end"><CircularProgress size={14} /></InputAdornment>
                ),
              }}
              sx={{ mb: 1.5, '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
            />
            <WalletPreview
              side="to"
              wallet={toWallet}
              loading={lookingUp.to}
              error={lookupError.to}
              projectedBalance={projectedTo}
            />
          </Grid>
        </Grid>

        {sameWallet && (
          <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
            Source and destination wallets must be different.
          </Alert>
        )}
        {(fromWallet?.is_locked || toWallet?.is_locked) && (
          <Alert severity="warning" sx={{ mb: 2, borderRadius: 2 }}>
            {fromWallet?.is_locked ? 'Source wallet is locked. ' : ''}
            {toWallet?.is_locked ? 'Destination wallet is locked.' : ''}
          </Alert>
        )}

        <Grid container spacing={2}>
          <Grid item xs={12} sm={5}>
            <TextField
              fullWidth required type="number"
              label="Amount to Transfer"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="0.00"
              disabled={submitting}
              inputProps={{ min: 0.01, step: 0.01, style: { fontSize: '1.1rem', fontWeight: 700 } }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Typography sx={{ fontWeight: 800, color: ACCE_BRAND.orange }}>₦</Typography>
                  </InputAdornment>
                ),
              }}
              error={amount.length > 0 && (!isValidAmount || insufficientBalance)}
              helperText={
                amount.length > 0 && !isValidAmount
                  ? 'Enter a valid amount (max 2 decimals)'
                  : insufficientBalance
                  ? `Insufficient balance. Max: ${formatCurrency(fromWallet.balance)}`
                  : ' '
              }
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
            />
          </Grid>
          <Grid item xs={12} sm={7}>
            <TextField
              fullWidth
              label="Description (optional)"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="e.g. Refund, Shared purchase settlement"
              disabled={submitting}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
            />
          </Grid>
        </Grid>

        {canSubmit && (
          <Paper
            variant="outlined"
            sx={{
              mt: 3, p: 2, borderRadius: 2,
              borderColor: alpha('#1976d2', 0.25),
              bgcolor: alpha('#1976d2', 0.03),
            }}
          >
            <Typography variant="subtitle2" fontWeight={800} sx={{ mb: 1, fontFamily: '"Inter", sans-serif', color: '#0F172A' }}>
              Transfer Summary
            </Typography>
            <Stack direction="row" justifyContent="space-between">
              <Typography variant="body2" color="text.secondary" fontWeight={600}>Amount</Typography>
              <Typography fontWeight={800} sx={{ color: '#1976d2', fontFamily: '"Inter", sans-serif' }}>
                {formatCurrency(amount)}
              </Typography>
            </Stack>
            <Stack direction="row" justifyContent="space-between" sx={{ mt: 0.5 }}>
              <Typography variant="body2" color="text.secondary" fontWeight={600}>From → To</Typography>
              <Typography variant="body2" fontWeight={700} sx={{ fontFamily: '"Inter", sans-serif' }}>
                {fromWallet.holder_name} → {toWallet.holder_name}
              </Typography>
            </Stack>
          </Paper>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 2, gap: 1 }}>
        <Button onClick={handleClose} disabled={submitting} color="inherit" sx={{ textTransform: 'none', fontWeight: 700 }}>
          Cancel
        </Button>
        <Button
          variant="contained"
          disabled={!canSubmit || submitting}
          onClick={handleSubmit}
          startIcon={submitting ? <CircularProgress size={18} color="inherit" /> : <SwapIcon />}
          sx={{
            ...glossyButton('#1976d2', '#0d47a1', '#42a5f5'),
            textTransform: 'none', fontWeight: 800, minWidth: 180,
          }}
        >
          {submitting ? 'Transferring…' : `Transfer ${amount ? formatCurrency(amount) : ''}`}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ════════════════════════════════════════════
// REVERSE LAST TRANSACTION DIALOG
// ════════════════════════════════════════════
const ReverseLastTransactionDialog = ({ open, wallet, lastTransaction, onClose, onReversed, pushSnackbar }) => {
  const [reason, setReason] = useState('');
  const [confirmText, setConfirmText] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      setReason('');
      setConfirmText('');
      setSubmitting(false);
    }
  }, [open, lastTransaction?.id]);

  const handleClose = useCallback(() => {
    if (submitting) return;
    onClose?.();
  }, [submitting, onClose]);

  const handleSubmit = useCallback(async () => {
    if (!wallet?.staff_id || !lastTransaction) return;
    if (reason.trim().length < 5) {
      pushSnackbar('Please provide a clear reason (min 5 characters).', 'warning');
      return;
    }
    if (confirmText !== 'REVERSE') {
      pushSnackbar('Type REVERSE to confirm.', 'warning');
      return;
    }

    setSubmitting(true);
    const res = await apiReverseLast(wallet.staff_id, {
      reason: reason.trim(),
      expected_txn_id: lastTransaction.id,
    });
    setSubmitting(false);

    if (!res.success) {
      pushSnackbar(res.error || 'Failed to reverse transaction.', 'error');
      return;
    }

    pushSnackbar(
      `Reversed ${lastTransaction.reference}. New balance: ${formatCurrency(res.wallet.balance)}`,
      'success',
    );
    onReversed?.(res);
    onClose?.();
  }, [wallet, lastTransaction, reason, confirmText, pushSnackbar, onReversed, onClose]);

  const isDebit = lastTransaction?.direction === 'debit';
  const canSubmit = !submitting && lastTransaction && confirmText === 'REVERSE' && reason.trim().length >= 5;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth
      PaperProps={{ sx: { borderRadius: 3 } }}>

      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2 }}>
          <Avatar sx={{ bgcolor: alpha('#f57c00', 0.12), color: '#f57c00' }}>
            <UndoIcon />
          </Avatar>
          <Box>
            <Typography variant="h6" fontWeight={800} fontFamily='"Inter", sans-serif'>
              Reverse Last Transaction
            </Typography>
            <Typography variant="caption" color="text.secondary" fontFamily='"Inter", sans-serif'>
              {wallet?.holder_name} · {wallet?.staff_id}
            </Typography>
          </Box>
        </Box>
        <IconButton onClick={handleClose} disabled={submitting}><CloseIcon /></IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ p: { xs: 2, sm: 3 } }}>
        {!lastTransaction ? (
          <Alert severity="info" sx={{ borderRadius: 2 }}>
            No reversible transaction found on this wallet.
          </Alert>
        ) : (
          <>
            <Alert severity="warning" icon={<WarningIcon />} sx={{ mb: 3, borderRadius: 2 }}>
              This will create a new opposite-direction entry to undo the effect.
              The original transaction is preserved for audit purposes.
            </Alert>

            <Typography variant="subtitle2" fontWeight={800} sx={{ mb: 1, fontFamily: '"Inter", sans-serif' }}>
              Transaction to Reverse
            </Typography>

            <Box
              sx={{
                p: 2, borderRadius: 2, border: '1px solid',
                borderColor: 'divider', bgcolor: '#fafafa', mb: 3,
              }}
            >
              <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={2}>
                <Box>
                  <Typography variant="caption" color="text.secondary" fontWeight={700}>Reference</Typography>
                  <Typography fontWeight={700} sx={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>
                    {lastTransaction.reference}
                  </Typography>
                </Box>
                <Chip
                  size="small"
                  label={prettifyTxType(lastTransaction.transaction_type)}
                  sx={{
                    fontWeight: 700, fontSize: '0.68rem',
                    bgcolor: alpha(isDebit ? '#f44336' : '#4caf50', 0.1),
                    color: isDebit ? '#c62828' : '#2e7d32',
                  }}
                />
              </Stack>

              <Divider sx={{ my: 1.5 }} />

              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Typography variant="caption" color="text.secondary" fontWeight={700}>Amount</Typography>
                <Typography fontWeight={800}
                  sx={{ color: isDebit ? '#c62828' : '#2e7d32', fontFamily: '"Inter", sans-serif' }}>
                  {isDebit ? '−' : '+'}{formatCurrency(lastTransaction.amount)}
                </Typography>
              </Stack>

              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mt: 0.5 }}>
                <Typography variant="caption" color="text.secondary" fontWeight={700}>Date</Typography>
                <Typography variant="body2" sx={{ fontFamily: '"Inter", sans-serif' }}>
                  {formatDateTime(lastTransaction.occurred_at || lastTransaction.created_at)}
                </Typography>
              </Stack>

              {lastTransaction.description && (
                <>
                  <Divider sx={{ my: 1.5 }} />
                  <Typography variant="caption" color="text.secondary" fontWeight={700}>Description</Typography>
                  <Typography variant="body2" sx={{ fontFamily: '"Inter", sans-serif' }}>
                    {lastTransaction.description}
                  </Typography>
                </>
              )}
            </Box>

            <TextField
              fullWidth required multiline minRows={2}
              label="Reason for reversal"
              placeholder="e.g. Wrong amount credited, funded wrong wallet, duplicate entry"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              disabled={submitting}
              sx={{ mb: 2, '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
            />

            <TextField
              fullWidth required
              label='Type "REVERSE" to confirm'
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value.toUpperCase())}
              disabled={submitting}
              error={confirmText.length > 0 && confirmText !== 'REVERSE'}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
            />
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 2, gap: 1 }}>
        <Button onClick={handleClose} disabled={submitting} color="inherit" sx={{ textTransform: 'none', fontWeight: 700 }}>
          Cancel
        </Button>
        <Button
          variant="contained"
          disabled={!canSubmit}
          onClick={handleSubmit}
          startIcon={submitting ? <CircularProgress size={18} color="inherit" /> : <UndoIcon />}
          sx={{
            ...glossyButton('#f57c00', '#e65100', '#ffb74d'),
            textTransform: 'none', fontWeight: 800, minWidth: 180,
          }}
        >
          {submitting ? 'Reversing…' : 'Reverse Transaction'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ════════════════════════════════════════════
// MANUAL DEBIT DIALOG
// ════════════════════════════════════════════
const REASON_CODES = [
  { value: 'charge',     label: 'Charge',     desc: 'Service fee or processing charge' },
  { value: 'adjustment', label: 'Adjustment', desc: 'Balance correction or reconciliation' },
  { value: 'penalty',    label: 'Penalty',    desc: 'Fine or disciplinary deduction' },
  { value: 'correction', label: 'Correction', desc: 'Fix an earlier entry' },
  { value: 'other',      label: 'Other',      desc: 'Specify in the reason note' },
];

const ManualDebitDialog = ({ open, wallet, onClose, onDebited, pushSnackbar }) => {
  const [amount, setAmount] = useState('');
  const [reasonCode, setReasonCode] = useState('charge');
  const [reasonNote, setReasonNote] = useState('');
  const [idempotencyKey, setIdempotencyKey] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      setAmount('');
      setReasonCode('charge');
      setReasonNote('');
      setIdempotencyKey(generateIdempotencyKey('debit'));
      setSubmitting(false);
    }
  }, [open, wallet?.staff_id]);

  const amountNum = Number(amount);
  const isValidAmount = /^\d+(\.\d{1,2})?$/.test(String(amount).trim()) && amountNum > 0;
  const insufficientBalance = wallet && isValidAmount && amountNum > Number(wallet.balance);
  const walletLocked = wallet?.is_locked;

  const canSubmit =
    !submitting &&
    wallet &&
    !walletLocked &&
    isValidAmount &&
    !insufficientBalance &&
    reasonNote.trim().length >= 3;

  const newBalance = wallet && isValidAmount
    ? round2(Number(wallet.balance) - amountNum)
    : null;

  const handleSubmit = useCallback(async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    const res = await apiManualDebit(wallet.staff_id, {
      amount: String(amount).trim(),
      reason_code: reasonCode,
      reason_note: reasonNote.trim(),
      idempotency_key: idempotencyKey,
    });
    setSubmitting(false);

    if (!res.success) {
      pushSnackbar(res.error || 'Debit failed.', 'error');
      return;
    }

    pushSnackbar(
      `Debited ${formatCurrency(amount)} from ${wallet.holder_name}. New balance: ${formatCurrency(res.wallet.balance)}`,
      'success',
    );
    onDebited?.(res);
    onClose?.();
  }, [canSubmit, wallet, amount, reasonCode, reasonNote, idempotencyKey, pushSnackbar, onDebited, onClose]);

  const handleClose = () => { if (!submitting) onClose?.(); };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth
      PaperProps={{ sx: { borderRadius: 3 } }}>

      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2 }}>
          <Avatar sx={{ bgcolor: alpha('#c62828', 0.12), color: '#c62828' }}>
            <RemoveCircleIcon />
          </Avatar>
          <Box>
            <Typography variant="h6" fontWeight={800} fontFamily='"Inter", sans-serif'>
              Debit Wallet (Manual)
            </Typography>
            <Typography variant="caption" color="text.secondary" fontFamily='"Inter", sans-serif'>
              {wallet?.holder_name} · {wallet?.staff_id}
            </Typography>
          </Box>
        </Box>
        <IconButton onClick={handleClose} disabled={submitting}><CloseIcon /></IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ p: { xs: 2, sm: 3 } }}>
        {wallet && (
          <Paper
            variant="outlined"
            sx={{
              p: 1.5, mb: 3, borderRadius: 2,
              borderColor: alpha('#c62828', 0.25),
              bgcolor: alpha('#c62828', 0.02),
            }}
          >
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Box>
                <Typography variant="caption" color="text.secondary" fontWeight={700}>Current Balance</Typography>
                <Typography variant="h6" fontWeight={800} sx={{ color: '#c62828', fontFamily: '"Inter", sans-serif' }}>
                  {formatCurrency(wallet.balance)}
                </Typography>
              </Box>
              {newBalance !== null && (
                <Box sx={{ textAlign: 'right' }}>
                  <Typography variant="caption" color="text.secondary" fontWeight={700}>After Debit</Typography>
                  <Typography variant="h6" fontWeight={800} sx={{ color: '#0F172A', fontFamily: '"Inter", sans-serif' }}>
                    {formatCurrency(newBalance)}
                  </Typography>
                </Box>
              )}
            </Stack>
          </Paper>
        )}

        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth required type="number"
              label="Amount to Debit"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="0.00"
              disabled={submitting || walletLocked}
              inputProps={{ min: 0.01, step: 0.01, style: { fontSize: '1.1rem', fontWeight: 700 } }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Typography sx={{ fontWeight: 800, color: '#c62828' }}>₦</Typography>
                  </InputAdornment>
                ),
              }}
              error={amount.length > 0 && (!isValidAmount || insufficientBalance)}
              helperText={
                amount.length > 0 && !isValidAmount
                  ? 'Enter a valid amount (max 2 decimals)'
                  : insufficientBalance
                  ? `Insufficient balance. Max: ${formatCurrency(wallet.balance)}`
                  : ' '
              }
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth required size="small" disabled={submitting || walletLocked}>
              <InputLabel sx={{ fontWeight: 700 }}>Reason Category</InputLabel>
              <Select
                value={reasonCode}
                label="Reason Category"
                onChange={(e) => setReasonCode(e.target.value)}
                sx={{ borderRadius: 2, fontWeight: 600 }}
              >
                {REASON_CODES.map((r) => (
                  <MenuItem key={r.value} value={r.value} sx={{ fontWeight: 600 }}>
                    <Box>
                      <Typography fontWeight={700} sx={{ fontSize: '0.85rem' }}>{r.label}</Typography>
                      <Typography variant="caption" color="text.secondary">{r.desc}</Typography>
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth required multiline minRows={2}
              label="Reason Note"
              placeholder="Explain why this debit is being made (visible in audit log)"
              value={reasonNote}
              onChange={(e) => setReasonNote(e.target.value)}
              disabled={submitting || walletLocked}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth size="small"
              label="Idempotency key (auto-generated)"
              value={idempotencyKey}
              onChange={(e) => setIdempotencyKey(e.target.value)}
              disabled={submitting || walletLocked}
              helperText="Prevents duplicate debits if the request is retried."
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              InputProps={{ sx: { fontFamily: 'monospace', fontSize: '0.8rem' } }}
            />
          </Grid>
        </Grid>

        {walletLocked && (
          <Alert severity="warning" sx={{ mt: 2, borderRadius: 2 }}>
            This wallet is <strong>locked</strong>. Unlock it before debiting.
          </Alert>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 2, gap: 1 }}>
        <Button onClick={handleClose} disabled={submitting} color="inherit" sx={{ textTransform: 'none', fontWeight: 700 }}>
          Cancel
        </Button>
        <Button
          variant="contained"
          disabled={!canSubmit}
          onClick={handleSubmit}
          startIcon={submitting ? <CircularProgress size={18} color="inherit" /> : <RemoveCircleIcon />}
          sx={{
            ...glossyButton('#c62828', '#8e0000', '#ef5350'),
            textTransform: 'none', fontWeight: 800, minWidth: 180,
          }}
        >
          {submitting ? 'Processing…' : `Debit ${amount ? formatCurrency(amount) : ''}`}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ════════════════════════════════════════════
// MAIN COMPONENT
// ════════════════════════════════════════════
const WalletManagement = () => {
  const [activeMenu, setActiveMenu] = useState('Wallet');

  const [wallets, setWallets] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [searchValue, setSearchValue] = useState('');
  const [searchDebounced, setSearchDebounced] = useState('');

  const [stats, setStats] = useState(null);

  const [fundingOpen, setFundingOpen] = useState(false);
  const [detailStaffId, setDetailStaffId] = useState(null);
  const [transferOpen, setTransferOpen] = useState(false);
  const [transferPrefill, setTransferPrefill] = useState(null);

  const [reverseState, setReverseState] = useState({ open: false, wallet: null, lastTxn: null, loading: false });
  const [debitState, setDebitState] = useState({ open: false, wallet: null });

  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const debounceRef = useRef(null);

  const pushSnackbar = useCallback((message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  }, []);

  const closeSnackbar = useCallback(() => setSnackbar((s) => ({ ...s, open: false })), []);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setSearchDebounced(searchValue.trim());
      setPage(0);
    }, 400);
    return () => clearTimeout(debounceRef.current);
  }, [searchValue]);

  const fetchWallets = useCallback(async () => {
    setLoading(true);
    const params = { page: page + 1, page_size: rowsPerPage };
    if (searchDebounced) params.search = searchDebounced;

    const res = await apiListWallets(params);
    setLoading(false);

    if (res.success) {
      setWallets(res.data || []);
      setTotal(res.total || 0);
    } else {
      pushSnackbar(res.error || 'Failed to load wallets', 'error');
      setWallets([]);
      setTotal(0);
    }
  }, [page, rowsPerPage, searchDebounced, pushSnackbar]);

  const fetchStats = useCallback(async () => {
    const res = await apiWalletStats();
    if (res.success) setStats(res.stats);
  }, []);

  useEffect(() => { fetchWallets(); }, [fetchWallets]);
  useEffect(() => { fetchStats(); }, [fetchStats]);

  const handleRefresh = useCallback(() => {
    fetchWallets();
    fetchStats();
    pushSnackbar('Refreshed', 'info');
  }, [fetchWallets, fetchStats, pushSnackbar]);

  const handleToggleLock = useCallback(async (staffId, currentLocked) => {
    const res = await apiToggleLock(staffId);
    if (!res.success) {
      pushSnackbar(res.error, 'error');
      return;
    }
    pushSnackbar(currentLocked ? 'Wallet unlocked.' : 'Wallet locked.', 'success');
    fetchWallets();
    fetchStats();
  }, [fetchWallets, fetchStats, pushSnackbar]);

  const openTransfer = useCallback((wallet = null) => {
    setTransferPrefill(wallet ? {
      staff_id:    wallet.staff_id,
      holder_name: wallet.holder_name,
      balance:     wallet.balance,
      is_locked:   wallet.is_locked,
    } : null);
    setTransferOpen(true);
  }, []);

  const openReverse = useCallback(async (wallet) => {
    setReverseState({ open: true, wallet, lastTxn: null, loading: true });
    const res = await apiWalletDetail(wallet.staff_id, { page: 1, page_size: 1 });
    if (!res.success) {
      pushSnackbar(res.error || 'Failed to load last transaction.', 'error');
      setReverseState({ open: false, wallet: null, lastTxn: null, loading: false });
      return;
    }
    const lastTxn = res.last_transaction || res.transactions?.data?.[0] || null;
    setReverseState({ open: true, wallet, lastTxn, loading: false });
  }, [pushSnackbar]);

  const closeReverse = useCallback(() => {
    setReverseState({ open: false, wallet: null, lastTxn: null, loading: false });
  }, []);

  const openDebit = useCallback((wallet) => {
    setDebitState({ open: true, wallet });
  }, []);

  const closeDebit = useCallback(() => {
    setDebitState({ open: false, wallet: null });
  }, []);

  const onAnyWalletChange = useCallback(() => {
    fetchWallets();
    fetchStats();
  }, [fetchWallets, fetchStats]);

  return (
    <>
      <div className="epos-wrapper">
        <div className="epos-main-layout">
          <Sidebar activeMenu={activeMenu} setActiveMenu={setActiveMenu} />

          <div className="epos-main">
            <Header />

            <div className="epos-content" style={{ background: '#f5f7fa' }}>
              {/* HERO */}
              <div className="hero-card">
                <div>
                  <div className="hero-title" style={{ color: ACCE_BRAND.orange, fontWeight: 800 }}>
                    💰 Wallet Management
                  </div>
                  <div className="hero-text-wrapper">
                    <div className="hero-tagline">
                      <span className="hero-tagline-icon">✦</span>
                      Fund · Transfer · Reverse · Adjust · auditable ledger
                    </div>
                  </div>
                </div>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  <Tooltip title="Refresh">
                    <IconButton
                      onClick={handleRefresh}
                      sx={{
                        bgcolor: alpha(ACCE_BRAND.orange, 0.1),
                        '&:hover': { bgcolor: alpha(ACCE_BRAND.orange, 0.2) },
                        borderRadius: 2,
                      }}
                    >
                      <RefreshIcon sx={{ color: ACCE_BRAND.orange }} />
                    </IconButton>
                  </Tooltip>
                  <Button
                    variant="contained"
                    startIcon={<SwapIcon />}
                    onClick={() => openTransfer(null)}
                    sx={{
                      ...glossyButton('#1976d2', '#0d47a1', '#42a5f5'),
                      textTransform: 'none', fontWeight: 800, borderRadius: 2, px: 2.5,
                    }}
                  >
                    Transfer
                  </Button>
                  <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => setFundingOpen(true)}
                    sx={{
                      ...glossyButton(ACCE_BRAND.orange, ACCE_BRAND.orangeDark, ACCE_BRAND.orangeLight),
                      textTransform: 'none', fontWeight: 800, borderRadius: 2, px: 2.5,
                    }}
                  >
                    Fund Wallet
                  </Button>
                </Box>
              </div>

              {/* STATS */}
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={6} sm={6} md={3}>
                  <StatCard
                    label="Total Wallets"
                    value={stats?.wallets_count ?? 0}
                    sub={`${stats?.locked_count ?? 0} locked`}
                    icon={<WalletIcon />}
                    color={ACCE_BRAND.orange}
                  />
                </Grid>
                <Grid item xs={6} sm={6} md={3}>
                  <StatCard
                    label="Total Balance"
                    value={formatCompactCurrency(stats?.total_balance || 0)}
                    sub="Across all wallets"
                    icon={<MoneyIcon />}
                    color="#2e7d32"
                  />
                </Grid>
                <Grid item xs={6} sm={6} md={3}>
                  <StatCard
                    label="Credits Today"
                    value={formatCompactCurrency(stats?.credits_today || 0)}
                    sub={`${stats?.transactions_today ?? 0} txns today`}
                    icon={<TrendingUpIcon />}
                    color="#2e7d32"
                  />
                </Grid>
                <Grid item xs={6} sm={6} md={3}>
                  <StatCard
                    label="Debits Today"
                    value={formatCompactCurrency(stats?.debits_today || 0)}
                    sub="POS spend"
                    icon={<TrendingDownIcon />}
                    color="#c62828"
                  />
                </Grid>
              </Grid>

              {/* SEARCH */}
              <Paper sx={{ p: 2, mb: 3, borderRadius: 2, display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
                <TextField
                  size="small"
                  fullWidth
                  placeholder="Search by student ID or name…"
                  value={searchValue}
                  onChange={(e) => setSearchValue(e.target.value)}
                  sx={{
                    flex: 1, minWidth: 240, maxWidth: 420,
                    '& .MuiOutlinedInput-root': { borderRadius: 2 },
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon fontSize="small" />
                      </InputAdornment>
                    ),
                    endAdornment: searchValue && (
                      <InputAdornment position="end">
                        <IconButton size="small" onClick={() => setSearchValue('')}>
                          <ClearIcon fontSize="small" />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
                <Typography variant="caption" color="text.secondary" fontWeight={700}>
                  {total} {total === 1 ? 'wallet' : 'wallets'}
                </Typography>
              </Paper>

              {loading && <LinearProgress sx={{ mb: 2, borderRadius: 1 }} />}

              {/* TABLE */}
              <Paper sx={{ borderRadius: 2, overflow: 'hidden' }}>
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow sx={{ bgcolor: alpha(ACCE_BRAND.orange, 0.04) }}>
                        <TableCell sx={{ fontWeight: 800, fontFamily: '"Inter", sans-serif' }}>Student</TableCell>
                        <TableCell sx={{ fontWeight: 800, fontFamily: '"Inter", sans-serif' }}>Student ID</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 800, fontFamily: '"Inter", sans-serif' }}>Balance</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 800, fontFamily: '"Inter", sans-serif' }}>Total In</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 800, fontFamily: '"Inter", sans-serif' }}>Total Out</TableCell>
                        <TableCell sx={{ fontWeight: 800, fontFamily: '"Inter", sans-serif' }}>Status</TableCell>
                        <TableCell sx={{ fontWeight: 800, fontFamily: '"Inter", sans-serif' }}>Last activity</TableCell>
                        <TableCell align="center" sx={{ fontWeight: 800, fontFamily: '"Inter", sans-serif' }}>Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {loading && wallets.length === 0 ? (
                        [...Array(5)].map((_, i) => (
                          <TableRow key={i}>
                            {[...Array(8)].map((__, j) => (
                              <TableCell key={j}><Skeleton variant="text" /></TableCell>
                            ))}
                          </TableRow>
                        ))
                      ) : wallets.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={8} align="center" sx={{ py: 6 }}>
                            <WalletIcon sx={{ fontSize: 56, color: 'text.disabled', opacity: 0.4, mb: 1 }} />
                            <Typography variant="h6" color="text.secondary" fontWeight={700} fontFamily='"Inter", sans-serif'>
                              No wallets yet
                            </Typography>
                            <Typography variant="body2" color="text.secondary" fontFamily='"Inter", sans-serif'>
                              Click <strong>Fund Wallet</strong> to credit a student.
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ) : (
                        wallets.map((w) => (
                          <TableRow key={w.id} hover>
                            <TableCell>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2 }}>
                                <Avatar sx={{
                                  bgcolor: alpha(ACCE_BRAND.orange, 0.15),
                                  color: ACCE_BRAND.orange,
                                  width: 36, height: 36, fontWeight: 800, fontSize: '0.85rem',
                                }}>
                                  {initials(w.holder_name)}
                                </Avatar>
                                <Typography fontWeight={700} sx={{ fontFamily: '"Inter", sans-serif', fontSize: '0.85rem' }}>
                                  {w.holder_name}
                                </Typography>
                              </Box>
                            </TableCell>
                            <TableCell>
                              <Chip
                                size="small"
                                label={w.staff_id}
                                sx={{ fontFamily: 'monospace', fontWeight: 700, fontSize: '0.7rem' }}
                              />
                            </TableCell>
                            <TableCell align="right">
                              <Typography fontWeight={800} sx={{ color: ACCE_BRAND.orange, fontFamily: '"Inter", sans-serif' }}>
                                {formatCurrency(w.balance)}
                              </Typography>
                            </TableCell>
                            <TableCell align="right" sx={{ color: '#2e7d32', fontWeight: 700 }}>
                              {formatCurrency(w.total_credited)}
                            </TableCell>
                            <TableCell align="right" sx={{ color: '#c62828', fontWeight: 700 }}>
                              {formatCurrency(w.total_debited)}
                            </TableCell>
                            <TableCell>
                              <Chip
                                size="small"
                                icon={w.is_locked ? <LockIcon sx={{ fontSize: 12 }} /> : <LockOpenIcon sx={{ fontSize: 12 }} />}
                                label={w.is_locked ? 'Locked' : 'Active'}
                                sx={{
                                  fontWeight: 700, fontSize: '0.65rem',
                                  bgcolor: alpha(w.is_locked ? '#f44336' : '#4caf50', 0.1),
                                  color: w.is_locked ? '#c62828' : '#2e7d32',
                                }}
                              />
                            </TableCell>
                            <TableCell sx={{ fontSize: '0.75rem', color: '#64748B' }}>
                              {formatDateTime(w.last_transaction_at)}
                            </TableCell>
                            <TableCell align="center">
                              <Tooltip title="History">
                                <IconButton size="small" onClick={() => setDetailStaffId(w.staff_id)}>
                                  <HistoryIcon fontSize="small" sx={{ color: '#1976d2' }} />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Transfer from this wallet">
                                <IconButton size="small" onClick={() => openTransfer(w)}>
                                  <SwapIcon fontSize="small" sx={{ color: '#1976d2' }} />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title={w.can_reverse_last ? 'Reverse last transaction' : 'No reversible transaction'}>
                                <span>
                                  <IconButton
                                    size="small"
                                    disabled={!w.can_reverse_last}
                                    onClick={() => openReverse(w)}
                                  >
                                    <UndoIcon fontSize="small" sx={{ color: w.can_reverse_last ? '#f57c00' : '#bdbdbd' }} />
                                  </IconButton>
                                </span>
                              </Tooltip>
                              <Tooltip title="Manual debit">
                                <IconButton size="small" onClick={() => openDebit(w)}>
                                  <RemoveCircleIcon fontSize="small" sx={{ color: '#c62828' }} />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title={w.is_locked ? 'Unlock wallet' : 'Lock wallet'}>
                                <IconButton size="small" onClick={() => handleToggleLock(w.staff_id, w.is_locked)}>
                                  {w.is_locked
                                    ? <LockOpenIcon fontSize="small" sx={{ color: '#2e7d32' }} />
                                    : <LockIcon fontSize="small" sx={{ color: '#f57c00' }} />}
                                </IconButton>
                              </Tooltip>
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
                <TablePagination
                  rowsPerPageOptions={[10, 25, 50, 100]}
                  component="div"
                  count={total}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  onPageChange={(_, p) => setPage(p)}
                  onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
                />
              </Paper>
            </div>
          </div>
        </div>
        <Footer />
      </div>

      {/* DIALOGS */}
      <FundingDialog
        open={fundingOpen}
        onClose={() => setFundingOpen(false)}
        onFunded={onAnyWalletChange}
        pushSnackbar={pushSnackbar}
      />

      <WalletDetailDialog
        open={Boolean(detailStaffId)}
        staffId={detailStaffId}
        onClose={() => setDetailStaffId(null)}
        pushSnackbar={pushSnackbar}
      />

      <TransferWalletDialog
        open={transferOpen}
        onClose={() => { setTransferOpen(false); setTransferPrefill(null); }}
        onTransferred={onAnyWalletChange}
        pushSnackbar={pushSnackbar}
        prefillFrom={transferPrefill}
      />

      <ReverseLastTransactionDialog
        open={reverseState.open}
        wallet={reverseState.wallet}
        lastTransaction={reverseState.lastTxn}
        onClose={closeReverse}
        onReversed={onAnyWalletChange}
        pushSnackbar={pushSnackbar}
      />

      <ManualDebitDialog
        open={debitState.open}
        wallet={debitState.wallet}
        onClose={closeDebit}
        onDebited={onAnyWalletChange}
        pushSnackbar={pushSnackbar}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={closeSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        TransitionComponent={Fade}
      >
        <Alert
          onClose={closeSnackbar}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: '100%', fontWeight: 700, fontFamily: '"Inter", sans-serif' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default WalletManagement;